import React from 'react';
import { motion } from 'framer-motion';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Search, Bell, Settings, ChevronDown, Filter, LayoutGrid } from 'lucide-react';
const areaData = [{
  name: 'Mon',
  engagement: 4000,
  baseline: 2400
}, {
  name: 'Tue',
  engagement: 3000,
  baseline: 1398
}, {
  name: 'Wed',
  engagement: 2000,
  baseline: 9800
}, {
  name: 'Thu',
  engagement: 2780,
  baseline: 3908
}, {
  name: 'Fri',
  engagement: 1890,
  baseline: 4800
}, {
  name: 'Sat',
  engagement: 2390,
  baseline: 3800
}, {
  name: 'Sun',
  engagement: 3490,
  baseline: 4300
}];
const barData = [{
  name: 'Safety',
  value: 85
}, {
  name: 'Efficacy',
  value: 45
}, {
  name: 'Dosing',
  value: 65
}, {
  name: 'Guidelines',
  value: 30
}];
export function Slide10Dashboard() {
  return <div className="w-full h-full flex items-center justify-center max-w-[90rem] mx-auto" data-id="element-261">
      <motion.div className="w-full h-[85%] rounded-2xl bg-white border border-snakker-border shadow-[0_20px_60px_rgba(13,15,20,0.08)] overflow-hidden flex flex-col" initial={{
      opacity: 0,
      y: 40,
      scale: 0.95
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.8,
      ease: 'easeOut'
    }} data-id="element-262">
        {/* Top Nav */}
        <div className="h-14 border-b border-snakker-border flex items-center justify-between px-6 bg-snakker-ink/[0.015]" data-id="element-263">
          <div className="flex items-center gap-6" data-id="element-264">
            <div className="flex items-center gap-2" data-id="element-265">
              <div className="w-5 h-5 rounded bg-snakker-primary" data-id="element-266" />
              <span className="font-display font-medium text-snakker-ink" data-id="element-267">
                Snakker
              </span>
            </div>
            <div className="h-4 w-px bg-snakker-border" data-id="element-268" />
            <div className="flex items-center gap-2 text-sm text-snakker-muted bg-snakker-ink/[0.04] px-3 py-1.5 rounded-md" data-id="element-269">
              <span data-id="element-270">Q3 Oncology Campaign</span>
              <ChevronDown className="w-4 h-4" data-id="element-271" />
            </div>
          </div>

          <div className="flex items-center gap-4 text-snakker-subtle" data-id="element-272">
            <Search className="w-4 h-4 hover:text-snakker-ink transition-colors" data-id="element-273" />
            <Bell className="w-4 h-4 hover:text-snakker-ink transition-colors" data-id="element-274" />
            <Settings className="w-4 h-4 hover:text-snakker-ink transition-colors" data-id="element-275" />
            <div className="w-7 h-7 rounded-full bg-snakker-ink/10 ml-2" data-id="element-276" />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden" data-id="element-277">
          {/* Sidebar */}
          <div className="w-64 border-r border-snakker-border p-4 flex flex-col gap-1 bg-snakker-ink/[0.015]" data-id="element-278">
            <div className="text-xs font-mono text-snakker-subtle mb-2 px-3 uppercase tracking-wider" data-id="element-279">
              Intelligence
            </div>
            {['Overview', 'Pathways', 'Segments', 'Curiosity Hotspots', 'Deviations'].map((item, i) => <div key={i} className={`px-3 py-2 rounded-md text-sm ${i === 0 ? 'bg-snakker-primary/10 text-snakker-primary font-medium' : 'text-snakker-muted hover:bg-snakker-ink/[0.04] hover:text-snakker-ink'}`} data-id="element-280">
                {item}
              </div>)}

            <div className="text-xs font-mono text-snakker-subtle mt-6 mb-2 px-3 uppercase tracking-wider" data-id="element-281">
              Content
            </div>
            {['Storyboards', 'Modules', 'Quizzes'].map((item, i) => <div key={i} className="px-3 py-2 rounded-md text-sm text-snakker-muted hover:bg-snakker-ink/[0.04] hover:text-snakker-ink" data-id="element-282">
                {item}
              </div>)}
          </div>

          {/* Dashboard Area */}
          <div className="flex-1 p-8 overflow-y-auto" data-id="element-283">
            <div className="flex justify-between items-end mb-8" data-id="element-284">
              <div data-id="element-285">
                <h1 className="text-2xl font-medium mb-1 text-snakker-ink" data-id="element-286">
                  Engagement Overview
                </h1>
                <p className="text-sm text-snakker-subtle" data-id="element-287">
                  Analyzing behavioral signals across 12,400 sessions.
                </p>
              </div>
              <div className="flex gap-3" data-id="element-288">
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-snakker-border text-sm text-snakker-muted hover:bg-snakker-ink/[0.04]" data-id="element-289">
                  <Filter className="w-4 h-4" data-id="element-290" /> Filter
                </button>
                <button className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-snakker-ink text-white text-sm font-medium hover:bg-snakker-ink/90" data-id="element-291">
                  <LayoutGrid className="w-4 h-4" data-id="element-292" /> Export Report
                </button>
              </div>
            </div>

            {/* Top Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6" data-id="element-293">
              {[{
              label: 'Active Sessions',
              val: '12,400',
              trend: '+14%'
            }, {
              label: 'Avg. Depth',
              val: '4.2 screens',
              trend: '+8%'
            }, {
              label: 'Curiosity Score',
              val: '78/100',
              trend: '+12%'
            }, {
              label: 'Intent Signals',
              val: '1,240',
              trend: '+5%'
            }].map((stat, i) => <div key={i} className="p-4 rounded-xl border border-snakker-border bg-white" data-id="element-294">
                  <div className="text-sm text-snakker-subtle mb-2" data-id="element-295">
                    {stat.label}
                  </div>
                  <div className="flex items-end justify-between" data-id="element-296">
                    <div className="text-2xl font-medium text-snakker-ink" data-id="element-297">
                      {stat.val}
                    </div>
                    <div className="text-xs text-emerald-600 font-medium" data-id="element-298">
                      {stat.trend}
                    </div>
                  </div>
                </div>)}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-3 gap-6" data-id="element-299">
              <div className="col-span-2 p-5 rounded-xl border border-snakker-border bg-white" data-id="element-300">
                <div className="text-sm font-medium mb-6 text-snakker-ink" data-id="element-301">
                  Engagement vs Baseline
                </div>
                <div className="h-64" data-id="element-302">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-303">
                    <AreaChart data={areaData} margin={{
                    top: 0,
                    right: 0,
                    left: -20,
                    bottom: 0
                  }} data-id="element-304">
                      <defs data-id="element-305">
                        <linearGradient id="colorEngage" x1="0" y1="0" x2="0" y2="1" data-id="element-306">
                          <stop offset="5%" stopColor="#0085bd" stopOpacity={0.3} data-id="element-307" />
                          <stop offset="95%" stopColor="#0085bd" stopOpacity={0} data-id="element-308" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(13,15,20,0.06)" vertical={false} data-id="element-309" />
                      <XAxis dataKey="name" stroke="rgba(13,15,20,0.4)" fontSize={12} tickLine={false} axisLine={false} data-id="element-310" />
                      <YAxis stroke="rgba(13,15,20,0.4)" fontSize={12} tickLine={false} axisLine={false} data-id="element-311" />
                      <Tooltip contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid rgba(13,15,20,0.08)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(13,15,20,0.06)'
                    }} itemStyle={{
                      color: '#0d0f14'
                    }} data-id="element-312" />
                      <Area type="monotone" dataKey="engagement" stroke="#0085bd" strokeWidth={2} fillOpacity={1} fill="url(#colorEngage)" data-id="element-313" />
                      <Area type="monotone" dataKey="baseline" stroke="rgba(13,15,20,0.3)" strokeWidth={2} strokeDasharray="5 5" fill="none" data-id="element-314" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="col-span-1 p-5 rounded-xl border border-snakker-border bg-white" data-id="element-315">
                <div className="text-sm font-medium mb-6 text-snakker-ink" data-id="element-316">
                  Deep-dive Interest
                </div>
                <div className="h-64" data-id="element-317">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-318">
                    <BarChart data={barData} layout="vertical" margin={{
                    top: 0,
                    right: 0,
                    left: 0,
                    bottom: 0
                  }} data-id="element-319">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(13,15,20,0.06)" horizontal={false} data-id="element-320" />
                      <XAxis type="number" hide data-id="element-321" />
                      <YAxis dataKey="name" type="category" stroke="rgba(13,15,20,0.7)" fontSize={12} tickLine={false} axisLine={false} width={70} data-id="element-322" />
                      <Tooltip cursor={{
                      fill: 'rgba(13,15,20,0.04)'
                    }} contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid rgba(13,15,20,0.08)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 16px rgba(13,15,20,0.06)'
                    }} data-id="element-323" />
                      <Bar dataKey="value" fill="#7c5cf5" radius={[0, 4, 4, 0]} barSize={20} data-id="element-324" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}